import React, { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Shield, ShieldCheck, ShieldOff, Copy, Check, KeyRound, RefreshCw, Eye, EyeOff, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../api/client';
import { Button } from '../ui/Button';

interface TwoFAStatus {
  twoFactorEnabled: boolean;
  recoveryCodesRemaining: number;
}

interface TwoFASetupData {
  qrCode: string;
  totpUri: string;
}

interface TwoFARecoveryCodes {
  recoveryCodes: string[];
}

export default function TwoFactorSettings() {
  const qc = useQueryClient();

  const { data: status } = useQuery<TwoFAStatus>({
    queryKey: ['2fa-status'],
    queryFn: () => apiGet<TwoFAStatus>('/auth/2fa/status'),
  });

  // Setup state
  const [setupStep, setSetupStep] = useState<'idle' | 'qr' | 'recovery'>('idle');
  const [qrCode, setQrCode] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);

  // Disable state
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisablePwd, setShowDisablePwd] = useState(false);

  // Regenerate state
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenCode, setRegenCode] = useState('');

  const setupMutation = useMutation<TwoFASetupData, Error, void>({
    mutationFn: () => apiPost<TwoFASetupData>('/auth/2fa/setup'),
    onSuccess: (data) => {
      setQrCode(data.qrCode);
      setTotpUri(data.totpUri);
      setSetupStep('qr');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const verifySetupMutation = useMutation<TwoFARecoveryCodes, Error, string>({
    mutationFn: (code: string) => apiPost<TwoFARecoveryCodes>('/auth/2fa/verify-setup', { code }),
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes);
      setSetupStep('recovery');
      qc.invalidateQueries({ queryKey: ['2fa-status'] });
      toast.success('2FA attivato con successo');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const disableMutation = useMutation<void, Error, { password: string; totpCode: string }>({
    mutationFn: ({ password, totpCode }) => apiPost('/auth/2fa/disable', { password, totpCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['2fa-status'] });
      setShowDisableModal(false);
      setDisablePassword('');
      setDisableCode('');
      setSetupStep('idle');
      toast.success('2FA disattivato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const regenMutation = useMutation<TwoFARecoveryCodes, Error, string>({
    mutationFn: (totpCode: string) => apiPost<TwoFARecoveryCodes>('/auth/2fa/recovery-codes/regenerate', { totpCode }),
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes);
      setShowRegenModal(false);
      setRegenCode('');
      qc.invalidateQueries({ queryKey: ['2fa-status'] });
      toast.success('Recovery codes rigenerati');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function copyToClipboard(text: string): boolean {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for HTTP (insecure context)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }

  const handleCopyRecoveryCodes = () => {
    copyToClipboard(recoveryCodes.join('\n'));
    setCopied(true);
    toast.success('Recovery codes copiati');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyTotpUri = () => {
    copyToClipboard(totpUri);
    toast.success('URI copiato');
  };

  const handleFinishSetup = () => {
    setSetupStep('idle');
    setSetupCode('');
  };

  if (!status) return null;

  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)' }}>
      <div style={{ padding: '0.875rem 1.25rem', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Shield size={16} style={{ color: 'var(--color-text-muted)' }} />
          <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>Autenticazione a due fattori</h3>
        </div>
      </div>

      <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Status badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          {status.twoFactorEnabled ? (
            <>
              <ShieldCheck size={18} style={{ color: 'var(--color-success)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-success)', fontWeight: 500 }}>
                2FA Attiva
              </span>
            </>
          ) : (
            <>
              <ShieldOff size={18} style={{ color: 'var(--color-warning)' }} />
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-warning)', fontWeight: 500 }}>
                2FA Disattivata
              </span>
            </>
          )}
        </div>

        {/* Recovery codes count */}
        {status.twoFactorEnabled && (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Recovery codes rimasti: {status.recoveryCodesRemaining}/10
            {status.recoveryCodesRemaining <= 3 && (
              <span style={{ color: 'var(--color-error)', marginLeft: '0.5rem', fontWeight: 600 }}>
                ⚠️ Pochi codici rimasti
              </span>
            )}
          </p>
        )}

        {/* Setup flow: QR */}
        {setupStep === 'qr' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Scansiona il QR code con la tua app autenticatore (Google Authenticator, Authy, 1Password)
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={qrCode} alt="QR Code 2FA" style={{ width: 200, height: 200 }} />
            </div>

            {/* Manual entry */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Inserimento manuale:</span>
              <button
                onClick={() => setShowSecret(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center' }}
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {showSecret && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <code style={{
                  flex: 1, fontSize: 'var(--text-xs)', padding: '0.375rem 0.5rem',
                  background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)',
                  border: '1px solid rgba(255,255,255,0.10)', fontFamily: 'monospace', wordBreak: 'break-all',
                }}>
                  {totpUri}
                </code>
                <button onClick={handleCopyTotpUri} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)' }}>
                  <Copy size={14} />
                </button>
              </div>
            )}

            {/* OTP verification */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)' }}>
                Inserisci codice OTP per confermare
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={setupCode}
                  onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{
                    flex: 1, background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-card)', color: 'var(--color-text)',
                    fontSize: 'var(--text-lg)', textAlign: 'center', padding: '0.5rem', fontFamily: 'monospace',
                    letterSpacing: '0.3em', outline: 'none',
                  }}
                  onFocus={e => (e.target.style.borderColor = 'var(--color-primary)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.10)')}
                />
                <Button
                  onClick={() => verifySetupMutation.mutate(setupCode)}
                  disabled={setupCode.length !== 6}
                  loading={verifySetupMutation.isPending}
                >
                  Conferma
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Setup flow: Recovery codes */}
        {setupStep === 'recovery' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)' }}>
            <div style={{
              background: 'var(--color-error-highlight)', border: '1px solid var(--color-error)',
              borderRadius: 'var(--radius-card)', padding: '0.625rem 0.875rem',
              fontSize: 'var(--text-sm)', color: 'var(--color-error)',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              <AlertTriangle size={16} />
              <span style={{ fontWeight: 600 }}>Mostrati una sola volta. Salvali in un posto sicuro.</span>
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: '0.375rem', fontFamily: 'monospace',
              fontSize: 'var(--text-sm)', padding: '0.5rem',
              background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}>
              {recoveryCodes.map((code, i) => (
                <div key={i} style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                  {code}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="secondary" onClick={handleCopyRecoveryCodes} icon={copied ? <Check size={14} /> : <Copy size={14} />}>
                {copied ? 'Copiato' : 'Copia tutti'}
              </Button>
              <Button onClick={handleFinishSetup}>
                Ho salvato i codici
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        {status.twoFactorEnabled && setupStep === 'idle' && (
          <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
            <Button
              variant="secondary" size="sm"
              onClick={() => setShowRegenModal(true)}
              icon={<RefreshCw size={14} />}
            >
              Rigenera recovery codes
            </Button>
            <Button
              variant="danger" size="sm"
              onClick={() => setShowDisableModal(true)}
              icon={<ShieldOff size={14} />}
            >
              Disattiva 2FA
            </Button>
          </div>
        )}

        {!status.twoFactorEnabled && setupStep === 'idle' && (
          <Button onClick={() => setupMutation.mutate()} loading={setupMutation.isPending}>
            Attiva 2FA
          </Button>
        )}

        {/* Disable modal */}
        {showDisableModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }} onClick={() => setShowDisableModal(false)}>
            <div style={{
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-container)', padding: '1.5rem', maxWidth: 400, width: '100%',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: '1rem' }}>Disattiva 2FA</h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                Inserisci la tua password e un codice TOTP per confermare.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showDisablePwd ? 'text' : 'password'}
                    value={disablePassword}
                    onChange={e => setDisablePassword(e.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    style={{
                      width: '100%', padding: '0.625rem 2.5rem 0.625rem 0.875rem',
                      background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                    }}
                  />
                  <button
                    onClick={() => setShowDisablePwd(v => !v)}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}
                  >
                    {showDisablePwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <input
                  type="text"
                  value={disableCode}
                  onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Codice TOTP (6 cifre)"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{
                    width: '100%', padding: '0.625rem 0.875rem',
                    background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => setShowDisableModal(false)}>Annulla</Button>
                  <Button
                    variant="danger" size="sm"
                    disabled={!disablePassword || disableCode.length !== 6}
                    loading={disableMutation.isPending}
                    onClick={() => disableMutation.mutate({ password: disablePassword, totpCode: disableCode })}
                  >
                    Disattiva
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regenerate modal */}
        {showRegenModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }} onClick={() => setShowRegenModal(false)}>
            <div style={{
              background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-container)', padding: '1.5rem', maxWidth: 400, width: '100%',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: '1rem' }}>Rigenera Recovery Codes</h3>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                I vecchi recovery codes verranno invalidati. Inserisci un codice TOTP per confermare.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  type="text"
                  value={regenCode}
                  onChange={e => setRegenCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Codice TOTP (6 cifre)"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{
                    width: '100%', padding: '0.625rem 0.875rem',
                    background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)',
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => setShowRegenModal(false)}>Annulla</Button>
                  <Button
                    size="sm"
                    disabled={regenCode.length !== 6}
                    loading={regenMutation.isPending}
                    onClick={() => regenMutation.mutate(regenCode)}
                  >
                    Rigenera
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
